import React, { useEffect, useRef, useState } from 'react'
import { Formik } from 'formik'
import moment from 'moment-timezone'
import { ExperienceInputField, ExperienceSelect } from '../form'
import QuestHOC from './QuestHOC'
import PropTypes from 'prop-types'
import {
  autosaveAfterFieldChange,
  queueAutosaveOnBlur,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'

const timezoneOptions = moment.tz.names().map((tz) => {
  const offset = moment.tz(tz).format('Z')
  return {
    value: tz,
    label: `${tz.replace(/_/g, ' ')} (GMT${offset})`,
  }
})

const valuesFromQuest = (questDetail) => ({
  duration: questDetail?.duration || '',
  groupSize: questDetail?.groupSize || '',
  location: questDetail?.location || '',
  locationTimezone:
    questDetail?.locationTimezone ||
    questDetail?.timezone ||
    questDetail?.timeZone ||
    '',
})

const buildPayload = (values) => ({
  duration: values.duration,
  groupSize: values.groupSize,
  location: values.location,
  locationTimezone: values.locationTimezone,
})

const QuestAdditionalDetails = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
  isSaving,
}) => {
  const busy = !!isFormSubmitting || !!isSaving
  const [initialValues, setInitialValues] = useState(() =>
    valuesFromQuest(questDetail),
  )
  const valuesRef = useRef(initialValues)

  useEffect(() => {
    const next = valuesFromQuest(questDetail)
    setInitialValues(next)
    valuesRef.current = next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questDetail?._id, questDetail?.id])

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        await runQueuedFormikSave({
          values,
          valuesRef,
          buildPayload,
          resetForm,
          setInitialValues,
          save: (payload) => updateQuestDetails(payload, { silent: true }),
        })
      }}>
      {(formik) => {
        const { handleSubmit, setFieldValue, dirty } = formik
        valuesRef.current = formik.values

        const fieldBlur = () => {
          queueAutosaveOnBlur(formik)
        }

        return (
          <form
            onSubmit={handleSubmit}
            className='additional-details-form'
            aria-busy={busy}>
            <FormikDirtyReporter id='setup-details' dirty={dirty} />
            <div className='experience-f-row'>
              <ExperienceInputField
                name='duration'
                label='Duration'
                placeholder='Calculated from dates'
                showErrorMessage
                capitalizeFirstLetter
                onBlur={fieldBlur}
              />
              <ExperienceInputField
                name='groupSize'
                label='Group size'
                placeholder='Add group size'
                showErrorMessage
                capitalizeFirstLetter
                onBlur={fieldBlur}
              />
              <ExperienceInputField
                name='location'
                label='Location'
                placeholder='Add location'
                showErrorMessage
                capitalizeFirstLetter
                onChange={(e) =>
                  setFieldValue('location', e.target.value.replace(/\d/g, ''))
                }
                onBlur={fieldBlur}
              />
            </div>
            <div className='experience-f-row'>
              <ExperienceSelect
                name='locationTimezone'
                label={"Quest Location's timezone"}
                options={timezoneOptions}
                placeholder='Add timezone'
                onChange={(e) => {
                  autosaveAfterFieldChange(
                    formik,
                    setFieldValue('locationTimezone', e.target.value, false),
                  )
                }}
                onBlur={fieldBlur}
              />
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

QuestAdditionalDetails.propTypes = {
  questDetail: PropTypes.shape({
    duration: PropTypes.string,
    groupSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    location: PropTypes.string,
    locationTimezone: PropTypes.string,
    timezone: PropTypes.string,
    timeZone: PropTypes.string,
  }),
  updateQuestDetails: PropTypes.func.isRequired,
  isFormSubmitting: PropTypes.bool,
  isSaving: PropTypes.bool,
}

export default QuestHOC(QuestAdditionalDetails)
